import type { ThunkDispatch } from '@reduxjs/toolkit'

import { apiRTK } from 'api/api-rtk'
import { automationApi } from 'api/automationApi'
import { notifyError } from 'shared/errors'
import { useMount } from 'shared/hooks'

import store from '../../../redux/store'

const listenErrors = ({ error }: ErrorEvent) => notifyError(error)

export function setupListeners(
	dispatch: ThunkDispatch<any, any, any>,
	{
		onFocus,
		onFocusLost,
		onOffline,
		onOnline
	}: Pick<
		typeof automationApi.internalActions,
		'onFocus' | 'onFocusLost' | 'onOffline' | 'onOnline'
	>
) {
	const controller = new AbortController()

	const handleVisibilityChange = () => {
		const isVisible = window.document.visibilityState === 'visible'
		const action = isVisible ? onFocus() : onFocusLost()
		dispatch(action)
	}

	const options = {
		signal: controller.signal
	}

	window.addEventListener('visibilitychange', handleVisibilityChange, options)
	window.addEventListener('focus', () => dispatch(onFocus()), options)

	window.addEventListener('online', () => dispatch(onOnline()), options)
	window.addEventListener('offline', () => dispatch(onOffline()), options)

	return () => controller.abort()
}

export const useListeners = () =>
	useMount(() => {
		window.addEventListener('error', listenErrors)

		const unsubscribeAutomation = setupListeners(
			store.dispatch,
			automationApi.internalActions
		)
		const unsubscribeApi = setupListeners(
			store.dispatch,
			apiRTK.internalActions
		)

		return () => {
			window.removeEventListener('error', listenErrors)
			unsubscribeAutomation()
			unsubscribeApi()
		}
	})
